import { Box, Typography, useMediaQuery } from '@mui/material'
import * as yup from 'yup'
import Input from 'components/Input/Input'
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator'
import Modal from 'components/Modal/Modal'
import { SelectDropdown } from 'components/SelectDropdown/SelectDropdown'
import { Field, Form, Formik } from 'formik'
import { useCountries } from 'hooks/useCountries'
import theme from 'lib/theme'
import { getCountries, isValidCoordinate } from 'lib/utils'
import { useTranslation } from 'react-i18next'
import { Country } from 'types/model/common'
import { usePostPlace } from 'hooks/usePlaces'
import { useNavigate } from 'react-router-dom'
import { RequiredLabel } from 'components/RequiredLabel/RequiredLabel'
import { useAlert } from 'providers/AlertProvider'

type NewPlaceModalProps = {
  modal: { right: boolean }
  handleCloseModal: () => void
}

type NewPlaceFormProps = {
  name: string
  retailerId: string
  currency: string
  street: string
  building: string
  city: string
  postcode: string
  country: string
  latitude: string
  longitude: string
}

export const NewPlaceModal = ({
  modal,
  handleCloseModal,
}: NewPlaceModalProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { loading, countries } = useCountries()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { mutateAsync, isLoading: isLoadingPostRequest } = usePostPlace()
  const { setAlert } = useAlert()

  const initialValues = {
    name: '',
    retailerId: '',
    currency: '',
    street: '',
    building: '',
    city: '',
    postcode: '',
    latitude: '',
    longitude: '',
    country: '',
  }

  const validationSchema = yup.object({
    name: yup
      .string()
      .required(t('required'))
      .min(2, t('atLeastTwoCharacters')),
    retailerId: yup.string().required(t('required')),
    currency: yup.string().required(t('required')),
    street: yup
      .string()
      .required(t('required'))
      .min(2, t('atLeastTwoCharacters')),
    building: yup.string(),
    city: yup
      .string()
      .required(t('required'))
      .min(2, t('atLeastTwoCharacters')),
    postcode: yup.string().required(t('required')),
    country: yup.string().required(t('required')),
    latitude: yup
      .string()
      .required(t('required'))
      .test('isLatitude', t('validLatitude'), (num) =>
        isValidCoordinate(num as string, 'latitude')
      ),
    longitude: yup
      .string()
      .required(t('required'))
      .test('isLongitude', t('validLongitude'), (num) =>
        isValidCoordinate(num as string, 'longitude')
      ),
  })

  const handleSubmit = async (values: NewPlaceFormProps) => {
    const country = countries.filter(
      (country: Country) => country.code === values.country
    )[0]
    const latitude = parseFloat(values.latitude)
    const longitude = parseFloat(values.longitude)
    const modifiedValues = {
      name: values.name,
      street: values.street,
      building: values.building,
      city: values.city,
      postcode: values.postcode,
      ext_id: values.retailerId,
      currency_code: values.currency,
      country,
      latitude,
      longitude,
    }
    await mutateAsync(modifiedValues, {
      onSuccess: (data) => {
        navigate(`/place/${data.data.data.masterId}/info`)
      },
      onError: () => {
        setAlert(t('failureMessage'), 'failure', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'right',
        })
      },
    })
  }

  if (loading) return <LoadingIndicator />
  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values) => handleSubmit(values)}>
      {({ errors, setFieldValue, values, isValid, dirty, submitForm }) => (
        <Modal
          showButton
          onButtonClick={submitForm}
          setModal={handleCloseModal}
          isLoadingRequest={isLoadingPostRequest}
          buttonIsDisabled={!isValid || !dirty}
          modal={modal}
          title={t('newPlace')}
          buttonText={t('createNewPlace')}>
          <Box
            display='flex'
            flexDirection='column'
            justifyContent='space-between'
            mb={3}
            sx={{ position: 'relative' }}>
            <Form>
              <Box
                display='flex'
                flexDirection='column'
                justifyContent='space-between'>
                <Box
                  display='flex'
                  flexDirection='column'
                  justifyContent='space-around'
                  mt={theme.spacing(3)}>
                  <Input
                    errorMessage={errors.name as string | undefined}
                    label={<RequiredLabel inputLabel='name' />}
                    name='name'
                    type='text'
                    variant='filled'
                    fullWidth
                    width='100%'
                  />
                  <Input
                    errorMessage={errors.retailerId as string | undefined}
                    label={<RequiredLabel inputLabel='retailerId' />}
                    name='retailerId'
                    type='text'
                    variant='filled'
                    fullWidth
                    width='100%'
                  />
                  <Input
                    errorMessage={errors.currency as string | undefined}
                    label={<RequiredLabel inputLabel='currencyInputLabel' />}
                    name='currency'
                    type='text'
                    variant='filled'
                    fullWidth
                    width='100%'
                  />
                  <Typography
                    variant='body2'
                    sx={{
                      marginBottom: theme.spacing(4),
                      marginTop: 0,
                    }}>
                    {t('address')}
                  </Typography>
                  <Box
                    sx={{ display: isMobile ? 'block' : 'flex' }}
                    justifyContent='space-between'>
                    <Input
                      errorMessage={errors.street as string | undefined}
                      label={<RequiredLabel inputLabel='street' />}
                      name='street'
                      type='text'
                      variant='filled'
                      width='66%'
                    />
                    <Input
                      errorMessage={errors.building as string | undefined}
                      label={t('building')}
                      name='building'
                      type='text'
                      variant='filled'
                      width='30%'
                    />
                  </Box>
                  <Box
                    sx={{ display: isMobile ? 'block' : 'flex' }}
                    justifyContent='space-between'>
                    <Input
                      errorMessage={errors.postcode as string | undefined}
                      label={<RequiredLabel inputLabel='postCode' />}
                      name='postcode'
                      type='text'
                      variant='filled'
                      width='30%'
                    />
                    <Input
                      errorMessage={errors.city as string | undefined}
                      label={<RequiredLabel inputLabel='city' />}
                      name='city'
                      type='text'
                      variant='filled'
                      width='66%'
                    />
                  </Box>
                  <Field
                    type='select'
                    component={SelectDropdown}
                    name='country'
                    selectedValue={values.country}
                    selectOptions={getCountries(countries)}
                    handleChange={(value: string) => {
                      setFieldValue('country', value)
                    }}
                    label={<RequiredLabel inputLabel='country' />}
                    style={{ width: '100%' }}
                  />
                  <Typography
                    variant='body2'
                    sx={{
                      marginBottom: theme.spacing(4),
                    }}>
                    {t('location')}
                  </Typography>
                  <Box
                    sx={{ display: isMobile ? 'block' : 'flex' }}
                    justifyContent='space-between'>
                    <Input
                      errorMessage={errors.latitude as string | undefined}
                      label={<RequiredLabel inputLabel='latitude' />}
                      name='latitude'
                      type='text'
                      variant='filled'
                      width='48%'
                    />
                    <Input
                      errorMessage={errors.longitude as string | undefined}
                      label={<RequiredLabel inputLabel='longitude' />}
                      name='longitude'
                      type='text'
                      variant='filled'
                      width='48%'
                    />
                  </Box>
                </Box>
              </Box>
            </Form>
          </Box>
        </Modal>
      )}
    </Formik>
  )
}
